import React, { useState } from 'react'
import { Card, Form, Button } from 'react-bootstrap'
import { SandboxInfo } from 'hooks/use-validate-hubspot-deal'
import { Sandbox } from 'interfaces/sandbox'
import useGetSandboxes from 'hooks/use-get-sandboxes'

interface HubspotValidationResult {
  isValid: boolean
  error?: string
  message?: string
}

interface HubSpotDealSectionProps {
  hubspotDealLink: string
  onHubspotDealLinkChange: (value: string) => void
  dealid?: string
  hubspotValidationResult: HubspotValidationResult | null
  onValidateDeal: () => void
  isValidatingHubspotLink: boolean
  currentUserName?: string
  dealName?: string
}

interface SettingsSectionProps {
  hasLinqOneInCart: boolean
  hasTrial: boolean
  onTrialChange: (value: boolean) => void
  allowQuantityAdjusting: boolean
  onAllowQuantityAdjustingChange: (value: boolean) => void
  allowCustomerUseReferralCode: boolean
  onAllowCustomerUseReferralCodeChange: (value: boolean) => void
  collectShippingAddress: boolean
  onCollectShippingAddressChange: (value: boolean) => void
}

interface SandboxSectionProps {
  sandboxInfo: SandboxInfo
}

interface SandboxSuggestionSectionProps {
  suggestions: SandboxInfo[]
  onConfirm: (suggestion: SandboxInfo) => void
  onDismiss: () => void
}

const ToggleSwitch: React.FC<{
  checked: boolean
  onChange: (checked: boolean) => void
}> = ({ checked, onChange }) => (
  <div
    onClick={() => onChange(!checked)}
    className="rounded-pill position-relative"
    style={{
      width: '50px',
      height: '24px',
      cursor: 'pointer',
      transition: 'background-color 0.3s',
      backgroundColor: checked ? '#2663eb' : '#6c757d',
    }}
  >
    <div
      className="bg-white rounded-circle position-absolute"
      style={{
        width: '20px',
        height: '20px',
        top: '2px',
        left: checked ? '28px' : '2px',
        transition: 'left 0.3s',
      }}
    />
  </div>
)

export const HubSpotDealSection: React.FC<HubSpotDealSectionProps> = ({
  hubspotDealLink,
  onHubspotDealLinkChange,
  dealid,
  hubspotValidationResult,
  onValidateDeal,
  isValidatingHubspotLink,
  currentUserName,
  dealName,
}) => {
  return (
    <Card className="mb-4 text-white bg-dark">
      <Card.Body>
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '1rem' }}>
          {dealName ? 'Deal' : currentUserName ? `${currentUserName}` : 'Deal'}
        </h2>

        {dealName ? (
          <>
            <div className="d-flex align-items-center gap-2">
              <img
                src="/Hubspot_logo.png"
                alt="HubSpot"
                style={{ width: '24px', height: '24px' }}
              />
              <span className="text-white fw-semibold fs-5">{dealName}</span>
              {isValidatingHubspotLink && (
                <span className="text-muted" style={{ fontSize: '14px' }}>
                  Validating...
                </span>
              )}
              {!isValidatingHubspotLink &&
                hubspotValidationResult &&
                hubspotValidationResult.isValid && (
                  <ion-icon
                    name="checkmark-circle"
                    style={{ color: '#10b981', fontSize: '20px' }}
                  />
                )}
              {!isValidatingHubspotLink &&
                hubspotValidationResult &&
                !hubspotValidationResult.isValid &&
                hubspotValidationResult.error && (
                  <ion-icon
                    name="close-circle"
                    style={{ color: '#ef4444', fontSize: '20px' }}
                  />
                )}
            </div>
          </>
        ) : (
          <>
            <div className="d-flex gap-2 mb-3">
              <Form.Control
                type="text"
                value={hubspotDealLink}
                onChange={(e) => onHubspotDealLinkChange(e.target.value)}
                placeholder="Enter HubSpot Deal Link"
                disabled={!!dealid}
              />
              <Button
                variant="primary"
                onClick={onValidateDeal}
                disabled={isValidatingHubspotLink || !hubspotDealLink.trim()}
              >
                {isValidatingHubspotLink ? 'Checking...' : 'Check Deal'}
              </Button>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  )
}

export const SettingsSection: React.FC<SettingsSectionProps> = ({
  hasLinqOneInCart,
  hasTrial,
  onTrialChange,
  allowQuantityAdjusting,
  onAllowQuantityAdjustingChange,
  allowCustomerUseReferralCode,
  onAllowCustomerUseReferralCodeChange,
  collectShippingAddress,
  onCollectShippingAddressChange,
}) => {
  return (
    <Card className="mb-4 text-white bg-dark">
      <Card.Body>
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '1rem' }}>
          Settings
        </h2>

        {hasLinqOneInCart && (
          <div className="d-flex justify-content-between align-items-center py-3 border-bottom">
            <div>
              <div className="fw-bold">Trial Period</div>
              <small className="text-muted">For Linq One products only</small>
            </div>
            <ToggleSwitch checked={hasTrial} onChange={onTrialChange} />
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center py-3 border-bottom">
          <div className="fw-bold">Adjustable Seat Quantity</div>
          <ToggleSwitch
            checked={allowQuantityAdjusting}
            onChange={onAllowQuantityAdjustingChange}
          />
        </div>

        <div className="d-flex justify-content-between align-items-center py-3 border-bottom">
          <div className="fw-bold">Allow Referral Code</div>
          <ToggleSwitch
            checked={allowCustomerUseReferralCode}
            onChange={onAllowCustomerUseReferralCodeChange}
          />
        </div>

        <div className="d-flex justify-content-between align-items-center py-3">
          <div className="fw-bold">Collect Shipping Address</div>
          <ToggleSwitch
            checked={collectShippingAddress}
            onChange={onCollectShippingAddressChange}
          />
        </div>
      </Card.Body>
    </Card>
  )
}

export const SandboxSuggestionSection: React.FC<
  SandboxSuggestionSectionProps
> = ({ suggestions, onConfirm, onDismiss }) => {
  return (
    <Card
      className="mb-4 text-white bg-dark"
      style={{ border: '1px solid #6366f1' }}
    >
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center gap-2">
            <ion-icon
              name="help-circle"
              style={{ color: '#6366f1', fontSize: '22px' }}
            />
            <h2
              style={{
                fontSize: '18px',
                fontWeight: 600,
                margin: 0,
                color: '#6366f1',
              }}
            >
              Possible Sandbox Match
            </h2>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={onDismiss}
            style={{ fontSize: '12px' }}
          >
            Not a match
          </Button>
        </div>

        <p className="text-muted mb-3" style={{ fontSize: '14px' }}>
          We found sandbox user(s) from the same company domain. Is one of them
          the same customer?
        </p>

        <table
          className="table table-dark table-sm mb-0"
          style={{ fontSize: '14px' }}
        >
          <thead>
            <tr className="text-muted">
              <th style={{ fontWeight: 'normal' }}>Email</th>
              <th style={{ fontWeight: 'normal' }}>Phone</th>
              <th style={{ fontWeight: 'normal' }}>Status</th>
              <th style={{ fontWeight: 'normal' }} />
            </tr>
          </thead>
          <tbody>
            {suggestions.map((suggestion) => (
              <tr key={suggestion.id}>
                <td>{suggestion.email}</td>
                <td>{suggestion.phone_number || 'Returned to Pool'}</td>
                <td style={{ textTransform: 'capitalize' }}>
                  {suggestion.status}
                </td>
                <td className="text-end">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => onConfirm(suggestion)}
                  >
                    Yes, same customer
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card.Body>
    </Card>
  )
}

export const SandboxSection: React.FC<SandboxSectionProps> = ({
  sandboxInfo,
}) => {
  return (
    <Card
      className="mb-4 text-white bg-dark"
      style={{ border: '1px solid #f59e0b' }}
    >
      <Card.Body>
        <div className="d-flex align-items-center gap-2 mb-3">
          <ion-icon
            name="alert-circle"
            style={{ color: '#f59e0b', fontSize: '22px' }}
          />
          <h2
            style={{
              fontSize: '18px',
              fontWeight: 600,
              margin: 0,
              color: '#f59e0b',
            }}
          >
            Sandbox User Selected
          </h2>
        </div>

        <div
          className="d-flex gap-4 p-2 rounded"
          style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
        >
          <div>
            <small className="text-muted">Email</small>
            <div>{sandboxInfo.email}</div>
          </div>
          <div>
            <small className="text-muted">Status</small>
            <div style={{ textTransform: 'capitalize' }}>
              {sandboxInfo.status}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

interface ManualSandboxSearchSectionProps {
  isEnabled: boolean
  onToggle: (enabled: boolean) => void
  onSelect: (sandbox: Sandbox) => void
}

export const ManualSandboxSearchSection: React.FC<
  ManualSandboxSearchSectionProps
> = ({ isEnabled, onToggle, onSelect }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [submittedQuery, setSubmittedQuery] = useState('')
  const [selectedSandbox, setSelectedSandbox] = useState<Sandbox | null>(null)

  const { data, isLoading } = useGetSandboxes({
    searchQuery: submittedQuery || undefined,
  })

  const sandboxes = submittedQuery
    ? (data?.sandboxes || []).filter((s) =>
        ['active', 'queued', 'expired'].includes(s.status)
      )
    : []

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setSubmittedQuery(searchQuery.trim())
    }
  }

  const handleSelect = (sandbox: Sandbox) => {
    setSelectedSandbox(sandbox)
    onSelect(sandbox)
  }

  const handleChange = () => {
    setSelectedSandbox(null)
    setSearchQuery('')
    setSubmittedQuery('')
  }

  return (
    <Card className="mb-4 text-white bg-dark">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center">
          <div className="fw-bold">Is this a sandbox user?</div>
          <ToggleSwitch checked={isEnabled} onChange={onToggle} />
        </div>

        {isEnabled && !selectedSandbox && (
          <div style={{ marginTop: '1rem' }}>
            <div className="d-flex gap-2 mb-3">
              <Form.Control
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) =>
                  e.key === 'Enter' && handleSearch()
                }
                placeholder="Search by email..."
              />
              <Button
                variant="primary"
                onClick={handleSearch}
                disabled={!searchQuery.trim() || isLoading}
              >
                {isLoading ? 'Searching...' : 'Search'}
              </Button>
            </div>

            {!isLoading && submittedQuery && sandboxes.length === 0 && (
              <div className="text-muted" style={{ fontSize: '14px' }}>
                No sandboxes found.
              </div>
            )}

            {!isLoading && sandboxes.length > 0 && (
              <table
                className="table table-dark table-sm mb-0"
                style={{ fontSize: '14px' }}
              >
                <thead>
                  <tr className="text-muted">
                    <th style={{ fontWeight: 'normal' }}>Email</th>
                    <th style={{ fontWeight: 'normal' }}>Phone</th>
                    <th style={{ fontWeight: 'normal' }}>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sandboxes.map((sandbox) => (
                    <tr key={sandbox.id}>
                      <td>{sandbox.email}</td>
                      <td>{sandbox.assigned_phone_number || 'N/A'}</td>
                      <td style={{ textTransform: 'capitalize' }}>
                        {sandbox.status}
                      </td>
                      <td className="text-end">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleSelect(sandbox)}
                        >
                          Select
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {isEnabled && selectedSandbox && (
          <div style={{ marginTop: '1rem' }}>
            <div
              className="d-flex justify-content-between align-items-center p-3 rounded"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
            >
              <div className="d-flex gap-4">
                <div>
                  <small className="text-muted">Email</small>
                  <div>{selectedSandbox.email}</div>
                </div>
                <div>
                  <small className="text-muted">Phone</small>
                  <div>{selectedSandbox.assigned_phone_number || 'N/A'}</div>
                </div>
                <div>
                  <small className="text-muted">Status</small>
                  <div style={{ textTransform: 'capitalize' }}>
                    {selectedSandbox.status}
                  </div>
                </div>
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleChange}
              >
                Change
              </Button>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}
